import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useMemo } from 'react';

import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useOrderbookStore } from '@/store/orderbook';

const DEFAULT_EXPIRATION = 60 * 5 * 1000;

export const EventExpiration = () => {
  const [parent] = useAutoAnimate();

  const { expiration, setExpiration } = useOrderbookStore();
  const now = useMemo(() => Date.now(), []);

  const dateObject = useMemo(
    () => (expiration ? new Date(expiration) : new Date()),
    [expiration],
  );

  const hours = useMemo(() => dateObject.getHours(), [dateObject]);
  const minutes = useMemo(() => dateObject.getMinutes(), [dateObject]);

  // Format human-readable time difference
  const humanReadableTime = useMemo(() => {
    if (!expiration) return '';

    const secondsFromNow = Math.floor((expiration - now) / 1000);

    // If expiration is in the past or now, show "No expiry"
    if (secondsFromNow <= 0) {
      return 'No expiry';
    }
    if (secondsFromNow < 60) {
      return 'Less than 1 minute';
    }
    // For minutes (less than 60 minutes)
    if (secondsFromNow < 60 * 60) {
      const minutes = Math.floor(secondsFromNow / 60);
      return `In ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }

    // For hours (less than 24 hours)
    if (secondsFromNow < 24 * 60 * 60) {
      const hours = Math.floor(secondsFromNow / (60 * 60));
      return `In ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }

    // For days
    const days = Math.floor(secondsFromNow / (24 * 60 * 60));
    return `In ${days} ${days === 1 ? 'day' : 'days'}`;
  }, [expiration, now]);

  // Handle date picker change
  const handleDateChange = (date: Date | undefined) => {
    if (!date) return;

    // Create a completely new date object with precise components
    const targetDate = new Date();

    // First set the date components
    targetDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

    // Set time components after date components
    targetDate.setHours(hours, minutes, 0, 0);

    // Set the expiration as a timestamp
    setExpiration(targetDate.getTime());
  };

  // Handle direct hour input
  const handleHourChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHour = Math.min(23, Math.max(0, parseInt(e.target.value) || 0));

    // Update expiration
    const targetDate = new Date(dateObject);
    targetDate.setHours(newHour);

    // Set the expiration as a timestamp
    setExpiration(targetDate.getTime());
  };

  // Handle direct minute input
  const handleMinuteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMinute = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));

    // Update expiration
    const targetDate = new Date(dateObject);
    targetDate.setMinutes(newMinute);

    // Set the expiration as a timestamp
    setExpiration(targetDate.getTime());
  };

  return (
    <div ref={parent} className="flex flex-col gap-5">
      <div className="flex flex-row text-[14px]">
        <div className="grow text-[#767676]">Set Expiration</div>
        <Switch
          checked={expiration !== null}
          onCheckedChange={() =>
            setExpiration(
              expiration === null
                ? new Date().getTime() + DEFAULT_EXPIRATION
                : null,
            )
          }
        />
      </div>

      {expiration !== null && (
        <div className="flex flex-col gap-5">
          <div className="flex items-center gap-2.5">
            <DatePicker
              className="h-[40px] w-full [&_button]:h-[40px] [&_button]:border-[#2B2B2B]"
              date={dateObject}
              // @ts-expect-error TYPE
              setDate={handleDateChange}
            />
            <div className="flex items-center">
              <Input
                type="number"
                min={0}
                max={23}
                value={hours}
                onChange={handleHourChange}
                className="h-[40px] w-[50px] text-center"
                wrapperClassName="[&>div]:h-[40px] [&>div]:border-[#2B2B2B]"
              />
              <span className="px-1">:</span>
              <Input
                type="number"
                min={0}
                max={59}
                value={minutes}
                onChange={handleMinuteChange}
                className="h-[40px] w-[50px] text-center"
                wrapperClassName="[&>div]:h-[40px] [&>div]:border-[#2B2B2B]"
              />
            </div>
          </div>
          <div className="flex items-center justify-between text-[14px]">
            <p className="text-secondary-light-gray">Expiration</p>
            <p>{humanReadableTime}</p>
          </div>
        </div>
      )}
    </div>
  );
};
